import { useState, useEffect } from "react";
import { EMPTY_CATS, loadPriority } from "../../lib/storage";

export default function ProTrackerScreen({trackerData,persistTracker,onResetPro}){
  const today=new Date().toISOString().slice(0,10);
  const blank={categories:JSON.parse(JSON.stringify(EMPTY_CATS)),history:[]};

  const data=trackerData||blank;
  function persist(next){persistTracker(next);}

  const [view,setView]=useState("dashboard");
  const [planPriority,setPlanPriority]=useState(()=>loadPriority());
  useEffect(()=>{ setPlanPriority(loadPriority()); },[]);
  const [activeCard,setActiveCard]=useState(null);
  const [inputVal,setInputVal]=useState("");
  const [inputMode,setInputMode]=useState("set");
  const [flashKey,setFlashKey]=useState(null);
  const [setupVals,setSetupVals]=useState({});
  const [setupTargets,setSetupTargets]=useState({});
  const [detailCat,setDetailCat]=useState(null);

  function latest(k){
    if(!data.history.length)return 0;
    return data.history[data.history.length-1].snapshot[k]||0;
  }
  function calcNW(snap){
    return Object.entries(data.categories).reduce((s,[k,c])=>s+(c.isAsset?1:-1)*(snap[k]||0),0);
  }
  function getDelta(k){
    if(data.history.length<2)return null;
    const prev=data.history[data.history.length-2].snapshot[k]||0;
    const cur=data.history[data.history.length-1].snapshot[k]||0;
    if(cur===prev)return null;
    const good=data.categories[k].isAsset?cur>prev:cur<prev;
    return{diff:cur-prev,good};
  }

  function applyChange(k){
    const amt=parseFloat(inputVal)||0;
    const cur=latest(k);
    let newVal=cur;
    if(inputMode==="set") newVal=amt;
    else if(inputMode==="add") newVal=cur+amt;
    else if(inputMode==="sub") newVal=Math.max(0,cur-amt);
    const snap={};
    Object.keys(data.categories).forEach(ck=>{ snap[ck]=latest(ck); });
    snap[k]=newVal;
    const hist=data.history.filter(h=>h.date!==today);
    persist({...data,history:[...hist,{date:today,snapshot:snap,note:""}].sort((a,b)=>a.date.localeCompare(b.date))});
    setFlashKey(k);
    setTimeout(()=>setFlashKey(null),1200);
    setActiveCard(null);
    setInputVal("");
    setInputMode("set");
  }

  const hasHistory=data.history.length>0;
  const curSnap=hasHistory?data.history[data.history.length-1].snapshot:{};
  const curNW=hasHistory?calcNW(curSnap):0;
  const prevNW=data.history.length>1?calcNW(data.history[data.history.length-2].snapshot):null;
  const nwDelta=prevNW!==null?curNW-prevNW:null;
  const totalAssets=Object.entries(data.categories).filter(([,c])=>c.isAsset).reduce((s,[k])=>s+latest(k),0);
  const totalDebt=Object.entries(data.categories).filter(([,c])=>!c.isAsset).reduce((s,[k])=>s+latest(k),0);

  const dinput={width:"100%",background:"#0f172a",border:"1.5px solid #334155",borderRadius:12,
    padding:"11px 14px",fontSize:16,fontWeight:700,color:"#f1f5f9",outline:"none",
    boxSizing:"border-box",fontFamily:"Nunito"};

  // ── Status helpers ──
  function getStatus(k){
    const cat=data.categories[k];
    const cur=latest(k);
    if(cat.isAsset){
      if(cat.target>0){
        const pct=cur/cat.target;
        if(pct>=0.8) return "green";
        if(pct>=0.4) return "yellow";
        return "red";
      }
      return cur>0?"green":"yellow";
    } else {
      if(cur===0) return "green";
      if(planPriority?.catPriority?.[0]===k) return "red";
      return "yellow";
    }
  }

  const STATUS={
    green:{ label:"On Track",     dot:"#10b981", bg:"#10b98120", border:"#10b98140", text:"#10b981" },
    yellow:{ label:"In Progress", dot:"#f59e0b", bg:"#f59e0b20", border:"#f59e0b40", text:"#f59e0b" },
    red:{   label:"Act Now",      dot:"#ef4444", bg:"#ef444420", border:"#ef444440", text:"#ef4444" },
  };

  function statusLabel(k){
    const s=getStatus(k);
    const cat=data.categories[k];
    if(!cat.isAsset&&latest(k)===0) return "Paid Off ✓";
    if(s==="yellow"&&cat.isAsset) return "Building";
    return STATUS[s].label;
  }

  /* ── SETUP ── */
  if(!hasHistory||view==="setup"){
    function finishSetup(){
      const snap={};
      const cats=JSON.parse(JSON.stringify(EMPTY_CATS));
      Object.keys(cats).forEach(k=>{
        snap[k]=parseFloat(setupVals[k])||0;
        cats[k].target=parseFloat(setupTargets[k])||0;
      });
      const next={...blank,categories:cats,history:[{date:today,snapshot:snap,note:"Starting point"}]};
      persist(next);setView("dashboard");
    }
    const assetKeys=Object.entries(EMPTY_CATS).filter(([,c])=>c.isAsset).map(([k])=>k);
    const debtKeys=Object.entries(EMPTY_CATS).filter(([,c])=>!c.isAsset).map(([k])=>k);
    const lbl={fontSize:12,fontWeight:800,color:"#94a3b8",textTransform:"uppercase",letterSpacing:1,marginBottom:5,display:"block"};
    return(
      <div style={{fontFamily:"Nunito",background:"#0f172a",minHeight:"100vh",paddingBottom:120}}>
        <div style={{background:"linear-gradient(135deg,#1e3a5f,#0f172a)",padding:"18px 16px 20px",borderBottom:"1px solid #1e293b"}}>
          <div style={{fontSize:11,color:"#64748b",fontWeight:800,textTransform:"uppercase",letterSpacing:2,marginBottom:4}}>First Time Setup</div>
          <div style={{fontSize:20,fontWeight:900,color:"#f1f5f9",fontFamily:"Fredoka One"}}>📊 Enter Your Starting Numbers</div>
          <div style={{fontSize:13,color:"#64748b",fontWeight:700,marginTop:4}}>These become your baseline — you'll update them anytime from the dashboard</div>
          {loadPriority()&&<div style={{marginTop:8,background:"#f59e0b22",border:"1px solid #f59e0b44",borderRadius:10,padding:"8px 12px",fontSize:12,fontWeight:800,color:"#f59e0b"}}>💡 Generate your plan first (🎯 My Plan) — it will auto-fill these numbers for you!</div>}
        </div>
        <div style={{padding:"16px",display:"flex",flexDirection:"column",gap:10}}>
          <div style={{fontSize:12,fontWeight:900,color:"#10b981",textTransform:"uppercase",letterSpacing:1.5}}>💰 Assets (what you own)</div>
          {assetKeys.map(k=>{const cat=EMPTY_CATS[k];return(
            <div key={k} style={{background:"#1e293b",borderRadius:14,padding:"14px",border:`1px solid ${cat.color}33`}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                <span style={{fontSize:20}}>{cat.emoji}</span>
                <span style={{fontSize:14,fontWeight:800,color:"#f1f5f9"}}>{cat.label}</span>
              </div>
              <label style={lbl}>Current Amount $</label>
              <input style={dinput} type="number" placeholder="0"
                value={setupVals[k]||""} onChange={e=>setSetupVals(v=>({...v,[k]:e.target.value}))}/>
              <label style={{...lbl,marginTop:10}}>Target Goal $ (optional)</label>
              <input style={{...dinput,borderColor:cat.color+"55"}} type="number" placeholder="e.g. 10000"
                value={setupTargets[k]||""} onChange={e=>setSetupTargets(v=>({...v,[k]:e.target.value}))}/>
            </div>
          );})}
          <div style={{fontSize:12,fontWeight:900,color:"#ef4444",textTransform:"uppercase",letterSpacing:1.5,marginTop:4}}>💳 Debts (what you owe)</div>
          {debtKeys.map(k=>{const cat=EMPTY_CATS[k];return(
            <div key={k} style={{background:"#1e293b",borderRadius:14,padding:"14px",border:`1px solid ${cat.color}33`}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                <span style={{fontSize:20}}>{cat.emoji}</span>
                <span style={{fontSize:14,fontWeight:800,color:"#f1f5f9"}}>{cat.label}</span>
              </div>
              <label style={lbl}>Current Balance $</label>
              <input style={dinput} type="number" placeholder="0"
                value={setupVals[k]||""} onChange={e=>setSetupVals(v=>({...v,[k]:e.target.value}))}/>
            </div>
          );})}
          <button onClick={finishSetup} className="btn-bounce"
            style={{width:"100%",background:"linear-gradient(135deg,#0ea5e9,#0c4a6e)",border:"none",
              borderRadius:16,padding:"16px",fontSize:16,fontWeight:900,color:"#fff",cursor:"pointer",
              boxShadow:"0 6px 20px #0ea5e944",marginTop:8}}>
            Start Tracking 🚀
          </button>
        </div>
      </div>
    );
  }

  /* ── HISTORY ── */
  if(view==="history"){
    return(
      <div style={{fontFamily:"Nunito",background:"#0f172a",minHeight:"100vh",paddingBottom:120}}>
        <div style={{background:"linear-gradient(135deg,#1e3a5f,#0f172a)",padding:"12px 16px 16px",borderBottom:"1px solid #1e293b",display:"flex",alignItems:"center",gap:12}}>
          <button onClick={()=>setView("dashboard")} className="btn-bounce"
            style={{background:"#0ea5e922",border:"2px solid #0ea5e955",borderRadius:12,padding:"6px 12px",fontSize:13,fontWeight:800,color:"#0ea5e9",cursor:"pointer"}}>← Back</button>
          <div style={{fontSize:16,fontWeight:900,color:"#f1f5f9"}}>Full History</div>
        </div>
        <div style={{padding:"16px",display:"flex",flexDirection:"column",gap:10}}>
          {[...data.history].reverse().map((entry,i,arr)=>{
            const nw=calcNW(entry.snapshot);
            const prev=arr[i+1];
            const delta=prev?nw-calcNW(prev.snapshot):null;
            return(
              <div key={entry.date} style={{background:"#1e293b",borderRadius:14,padding:"14px",border:"1px solid #334155"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                  <div>
                    <div style={{fontSize:12,color:"#475569",fontWeight:800}}>{entry.date}</div>
                    {entry.note&&<div style={{fontSize:12,color:"#64748b",fontWeight:700,marginTop:3}}>📝 {entry.note}</div>}
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:16,fontWeight:900,color:nw>=0?"#10b981":"#ef4444"}}>
                      {nw>=0?"$":"−$"}{Math.abs(Math.round(nw)).toLocaleString()}
                    </div>
                    {delta!==null&&<div style={{fontSize:12,fontWeight:800,color:delta>=0?"#10b981":"#ef4444",marginTop:2}}>
                      {delta>=0?"▲ +$":"▼ −$"}{Math.abs(Math.round(delta)).toLocaleString()}
                    </div>}
                  </div>
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                  {Object.entries(data.categories).filter(([k])=>(entry.snapshot[k]||0)>0).map(([k,cat])=>(
                    <div key={k} style={{background:"#0f172a",borderRadius:8,padding:"3px 9px",
                      fontSize:11,fontWeight:800,color:cat.color}}>
                      {cat.emoji} ${(entry.snapshot[k]||0).toLocaleString()}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  /* ── DETAIL ── */
  if(view==="detail"&&detailCat){
    const cat=data.categories[detailCat];
    const cur=latest(detailCat);
    const first=data.history[0]?.snapshot[detailCat]||0;
    const pts=data.history.map(h=>({date:h.date,v:h.snapshot[detailCat]||0}));
    const peakDetail=data.history.reduce((mx,h)=>Math.max(mx,h.snapshot[detailCat]||0),0);
    const baselineDetail=Math.max(first,peakDetail,cur);
    const prog=cat.isAsset&&cat.target>0?Math.min(cur/cat.target,1):!cat.isAsset&&baselineDetail>0?Math.min(Math.max(0,(baselineDetail-cur)/baselineDetail),1):null;
    return(
      <div style={{fontFamily:"Nunito",background:"#0f172a",minHeight:"100vh",paddingBottom:120}}>
        <div style={{background:"linear-gradient(135deg,#1e3a5f,#0f172a)",padding:"12px 16px 16px",borderBottom:"1px solid #1e293b",display:"flex",alignItems:"center",gap:12}}>
          <button onClick={()=>setView("dashboard")} className="btn-bounce"
            style={{background:`${cat.color}22`,border:`2px solid ${cat.color}55`,borderRadius:12,padding:"6px 12px",fontSize:13,fontWeight:800,color:cat.color,cursor:"pointer"}}>← Back</button>
          <div style={{fontSize:16,fontWeight:900,color:"#f1f5f9"}}>{cat.emoji} {cat.label}</div>
        </div>
        <div style={{padding:"16px",display:"flex",flexDirection:"column",gap:12}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
            {[
              {l:"Now",v:"$"+cur.toLocaleString(),c:cat.color},
              {l:"Started",v:"$"+first.toLocaleString(),c:"#475569"},
              {l:cat.isAsset?"Gained":"Paid Off",v:(()=>{const d=cat.isAsset?cur-first:first-cur;return(d>=0?"+$":"−$")+Math.abs(d).toLocaleString();})(),c:cat.isAsset?(cur>=first?"#10b981":"#ef4444"):(cur<=first?"#10b981":"#ef4444")},
            ].map(p=>(
              <div key={p.l} style={{background:"#1e293b",borderRadius:12,padding:"12px 8px",textAlign:"center",border:`1px solid ${cat.color}22`}}>
                <div style={{fontSize:15,fontWeight:900,color:p.c}}>{p.v}</div>
                <div style={{fontSize:10,color:"#475569",fontWeight:700,marginTop:2,textTransform:"uppercase"}}>{p.l}</div>
              </div>
            ))}
          </div>
          {prog!==null&&(
            <div style={{background:"#1e293b",borderRadius:14,padding:"14px",border:`1px solid ${cat.color}33`}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                <span style={{fontSize:13,fontWeight:800,color:"#f1f5f9"}}>{cat.isAsset?"Goal Progress":"Debt Elimination"}</span>
                <span style={{fontSize:13,fontWeight:900,color:cat.color}}>{Math.round(prog*100)}%</span>
              </div>
              <div style={{background:"#0f172a",borderRadius:20,height:10,overflow:"hidden",marginBottom:8}}>
                <div style={{width:`${prog*100}%`,height:"100%",background:`linear-gradient(90deg,${cat.color},${cat.color}cc)`,borderRadius:20,transition:"width 0.5s"}}/>
              </div>
              <div style={{fontSize:12,color:"#64748b",fontWeight:700}}>
                {cat.isAsset?"$"+(cat.target-cur).toLocaleString()+" to go toward $"+cat.target.toLocaleString()+" goal":cur>0?"$"+cur.toLocaleString()+" remaining · $"+(baselineDetail-cur).toLocaleString()+" paid off":"🎉 Debt eliminated!"}
              </div>
            </div>
          )}
          {/* ── RECOMMENDATIONS ── */}
          {(()=>{
            const pp=loadPriority();
            const risk=pp?.risk||"medium";
            const surplus=pp?.surplus||0;
            const planExpenses=pp?.expenses||0;

            const RECS={
              creditCard:{
                conservative:[
                  {title:"Avalanche Method — Pay Highest Rate First",tip:"List all cards by interest rate. Pay minimums on all, then throw every extra dollar at the highest-rate card. Mathematically saves the most money.",action:"Calculate your minimum payments, then add your full monthly surplus ($"+Math.round(surplus)+") to the highest-rate card."},
                  {title:"Stop Using the Card",tip:"Put the card in a drawer — or freeze it in a block of ice. You cannot out-pay a card you keep charging.",action:"Switch to debit or cash for all purchases until the balance hits zero."},
                  {title:"Call and Request a Lower Rate",tip:"Card issuers reduce rates for customers in good standing more often than people realize. One 5-minute call can save hundreds.",action:"Call the number on the back of your card. Say: 'I've been a customer for X years and would like a rate reduction.'"},
                ],
                medium:[
                  {title:"Avalanche Method — Pay Highest Rate First",tip:"List all cards by interest rate. Pay minimums on all, then throw every extra dollar at the highest-rate card.",action:"Put your full $"+Math.round(surplus)+" monthly surplus toward the highest-rate card after minimums."},
                  {title:"Balance Transfer to 0% APR Card",tip:"Many cards offer 0% intro APR for 12–18 months on balance transfers. Every payment goes to principal — not interest.",action:"Search 'best 0% APR balance transfer cards' and compare transfer fees (usually 3–5%)."},
                  {title:"Debt Consolidation Loan",tip:"A personal loan at 8–12% beats a credit card at 20–29%. One payment, lower rate, fixed payoff date.",action:"Check rates at your bank or at LendingClub, SoFi, or Marcus — no hard credit pull to see your rate."},
                ],
                aggressive:[
                  {title:"Avalanche + Side Income Attack",tip:"Avalanche method plus any extra income — freelance, overtime, selling items — goes entirely to the card balance.",action:"Set a 90-day sprint goal: eliminate this balance entirely. Sell unused items, pick up extra shifts."},
                  {title:"0% Balance Transfer + Aggressive Paydown",tip:"Transfer the balance and divide the full amount by the number of 0% months. That's your required monthly payment.",action:"Transfer balance, set up autopay for the exact amount needed to clear it before the intro period ends."},
                  {title:"Eliminate Subscriptions to Accelerate",tip:"Audit every recurring charge. Cutting $150/month in subscriptions adds $1,800/year directly to debt paydown.",action:"Review bank statements for all recurring charges. Cancel anything non-essential until this debt is gone."},
                ],
              },
              student:{
                conservative:[
                  {title:"Income-Driven Repayment (IDR)",tip:"Federal loans offer IDR plans that cap payments at 5–10% of discretionary income. Can significantly lower monthly payment.",action:"Visit studentaid.gov to compare IDR plans and apply. Takes about 20 minutes."},
                  {title:"Pay a Little Extra Each Month",tip:"Even $50–100 extra per month reduces total interest significantly and shortens your payoff timeline.",action:"Add $"+Math.min(100,Math.round(surplus*0.25))+" to your monthly payment — applied directly to principal."},
                  {title:"Public Service Loan Forgiveness",tip:"Work for a government or non-profit employer for 10 years while making qualifying payments and the remaining balance is forgiven.",action:"Check eligibility at studentaid.gov/PSLF — if you qualify, it's one of the best financial moves available."},
                ],
                medium:[
                  {title:"Refinance if Rate > 6%",tip:"Private refinancing can drop your rate to 4–6% if you have good credit and stable income. Only refinance federal loans if you don't need IDR or forgiveness.",action:"Compare rates at Earnest, SoFi, or Laurel Road — 2 minutes with a soft credit check."},
                  {title:"Avalanche: Add Surplus to Highest-Rate Loan",tip:"If you have multiple loans, focus extra payments on the highest rate while paying minimums on others.",action:"Direct $"+Math.round(surplus*0.5)+"/month extra to the highest-rate loan and set up autopay."},
                  {title:"Biweekly Payments",tip:"Pay half your monthly payment every two weeks. This results in one extra full payment per year with no lifestyle change.",action:"Set up biweekly autopay for exactly half your monthly payment amount."},
                ],
                aggressive:[
                  {title:"Refinance + Maximum Extra Payments",tip:"Get the lowest rate possible through refinancing, then put every available dollar toward the balance.",action:"Refinance, then add your full surplus $"+Math.round(surplus)+" beyond minimums each month."},
                  {title:"Lump Sum When Possible",tip:"Tax refunds, bonuses, and any windfalls go directly to principal — not lifestyle.",action:"Commit any income outside your regular paycheck to loan principal this year."},
                  {title:"5-Year Payoff Sprint",tip:"Calculate what monthly payment pays this off in 5 years. Make that your target — it's often achievable with income growth.",action:"Use studentloanplanner.com to model a 5-year payoff and see exactly what it requires."},
                ],
              },
              carLoan:{
                conservative:[
                  {title:"Pay Minimums and Redirect Surplus Elsewhere",tip:"Car loans are often 3–7%. If your rate is under 5%, the math usually favors investing surplus rather than prepaying.",action:"Check your rate. If under 5%, keep minimum payment and redirect surplus to investments."},
                  {title:"Avoid Trading Up When Paid Off",tip:"The moment a car is paid off, keep driving it. That freed-up payment becomes powerful savings.",action:"Commit now: when this loan is paid off, invest the full monthly payment amount instead of upgrading."},
                ],
                medium:[
                  {title:"Round Up Payments",tip:"Round your payment up to the nearest $50 or $100. Applied to principal, this shortens your loan meaningfully.",action:"Pay $"+Math.round(Math.ceil((latest(detailCat)||300)/50)*50)+"/month instead of the exact minimum."},
                  {title:"One Extra Payment Per Year",tip:"One extra full payment per year reduces a 5-year loan by 4–6 months and saves noticeable interest.",action:"Set a calendar reminder to make one extra payment in the month you receive a tax refund or bonus."},
                ],
                aggressive:[
                  {title:"Pay It Off Early — Free Up Cash Flow",tip:"Eliminating the car payment frees up significant monthly cash flow to redirect to wealth building.",action:"Calculate payoff amount (call lender for exact figure) and set a target payoff date within 12 months."},
                  {title:"Sell and Downgrade",tip:"If your car value exceeds the loan balance, selling and buying a cheaper car outright eliminates the payment entirely.",action:"Check your car's value at KBB.com and compare to your loan payoff amount."},
                ],
              },
              other:{
                conservative:[
                  {title:"Identify and List All Debts",tip:"You can't manage what you haven't mapped. List every debt with balance, rate, and minimum payment.",action:"Make a list in a notes app: lender, balance, rate, monthly minimum. This is your debt inventory."},
                  {title:"Minimum Payments Plus a Little",tip:"Even 10% extra on each payment reduces total interest and loan duration meaningfully.",action:"Add $"+Math.round(Math.max(20,surplus*0.1))+" to each debt's minimum payment this month."},
                ],
                medium:[
                  {title:"Snowball Method — Smallest Balance First",tip:"Pay minimums on all, put every extra dollar toward the smallest balance. When it's gone, roll that payment to the next.",action:"Identify your smallest balance and add $"+Math.round(surplus*0.5)+" to it monthly until eliminated."},
                  {title:"Consolidate Into a Lower-Rate Loan",tip:"A debt consolidation loan simplifies payments and often lowers the average interest rate.",action:"Check personal loan rates at Marcus by Goldman Sachs or your bank. Compare total interest cost."},
                ],
                aggressive:[
                  {title:"Debt Elimination Sprint",tip:"Set a 6-month goal to eliminate the smallest debts completely, creating momentum and freeing cash flow.",action:"Map all debts, target the smallest, and attack with your full $"+Math.round(surplus)+" surplus."},
                ],
              },
              savings:{
                conservative:[
                  {title:"High-Yield Savings Account (HYSA)",tip:"Standard bank savings pays 0.01%. HYSAs at online banks pay 4–5% APY with full FDIC protection.",action:"Open an account at Marcus, Ally, or SoFi. Takes 10 minutes. Earns 400x more than a traditional savings account."},
                  {title:"Automate Your Savings",tip:"Set up automatic transfer on payday — even $"+Math.round(Math.max(50,surplus*0.3))+"/month. You can't spend what you don't see.",action:"Log in to your bank today and schedule a recurring transfer on the same day you get paid."},
                  {title:"Keep 3–6 Months of Expenses",tip:"Your target emergency fund is $"+Math.round(planExpenses*6).toLocaleString()+". That's your savings anchor — build to this before investing heavily.",action:"Calculate your monthly expenses. Multiply by 6. That's your savings target."},
                ],
                medium:[
                  {title:"HYSA + I-Bonds Combination",tip:"Keep 3 months in HYSA for immediate access. Put additional savings in I-Bonds which adjust with inflation.",action:"Max I-Bonds at $10,000/year at TreasuryDirect.gov — they're inflation-protected and risk-free."},
                  {title:"Automate and Increase Annually",tip:"Automate savings and commit to increasing the amount by 1% of income every year.",action:"Set a calendar reminder every January to increase your monthly auto-transfer by one percent."},
                  {title:"Savings Rate Target: 20%",tip:"A 20% savings rate builds substantial wealth over time.",action:"Calculate 20% of your monthly take-home pay. Set that as your savings auto-transfer target."},
                ],
                aggressive:[
                  {title:"HYSA for Emergency Fund, Invest the Rest",tip:"Cash sitting above 6 months of expenses is losing to inflation. Once funded, redirect aggressively to investments.",action:"Calculate $"+Math.round(planExpenses*6).toLocaleString()+" target. Every dollar above that gets invested."},
                  {title:"50/30/20 Flipped: 50% Savings Rate",tip:"Aggressive wealth builders often target 30–50% savings rates by cutting lifestyle costs early.",action:"Audit your top 3 biggest expenses and find one to reduce this month."},
                ],
              },
              emergency:{
                conservative:[
                  {title:"Build to 6 Months of Expenses First",tip:"Your target is $"+Math.round(planExpenses*6).toLocaleString()+". This is your financial foundation — nothing else matters until this exists.",action:"Open a dedicated HYSA labeled 'Emergency Fund Only.' Auto-transfer $"+Math.round(Math.max(50,surplus*0.5))+"/month."},
                  {title:"Keep It Completely Separate",tip:"Emergency fund money must be in a different account than your regular savings — invisible and inconvenient to access.",action:"Open a separate HYSA at a different bank than your checking account. Out of sight, out of mind."},
                  {title:"Never Invest Emergency Money",tip:"Emergency funds belong in cash, not the stock market. A market crash is often when emergencies happen.",action:"HYSA only. Target 4–5% APY at Ally, Marcus, or SoFi. Zero risk, FDIC insured."},
                ],
                medium:[
                  {title:"3-Month Sprint, Then 6-Month Target",tip:"Get to 3 months first ($"+Math.round(planExpenses*3).toLocaleString()+") for basic protection, then continue to 6 months.",action:"Set milestone 1: $"+Math.round(planExpenses*3).toLocaleString()+". Celebrate when you hit it. Then continue to $"+Math.round(planExpenses*6).toLocaleString()+"."},
                  {title:"HYSA With 4-5% APY",tip:"Your emergency fund should earn money while it sits. Online banks pay significantly more than traditional banks.",action:"Open account at Ally or Marcus — takes 10 minutes, earns 4-5% APY, FDIC insured."},
                  {title:"Automate $"+Math.round(surplus*0.4)+"/Month",tip:"Treat your emergency fund contribution like a bill — non-negotiable, automated.",action:"Set up recurring transfer of $"+Math.round(surplus*0.4)+" on payday to your emergency HYSA."},
                ],
                aggressive:[
                  {title:"Hit 3 Months Fast, Then Invest",tip:"With an aggressive profile, reach 3-month emergency fund quickly, then shift surplus to investing.",action:"Sprint to $"+Math.round(planExpenses*3).toLocaleString()+" in 3–6 months, then redirect surplus to investments."},
                  {title:"Don't Over-Fund — Invest the Rest",tip:"Above 6 months of expenses, cash is a drag on your portfolio. Excess emergency fund money costs you investment returns.",action:"Set a hard ceiling at $"+Math.round(planExpenses*6).toLocaleString()+". Every dollar above goes to investments."},
                ],
              },
              invest:{
                conservative:[
                  {title:"Start With a Target-Date Fund",tip:"One fund, automatic rebalancing, age-appropriate allocation. Perfect for conservative investors who want simplicity.",action:"Open a Roth IRA at Fidelity or Vanguard and buy FXIFX (Fidelity) or VTTSX (Vanguard) — done."},
                  {title:"Max Your Roth IRA First ($7,000/year)",tip:"Tax-free growth forever. The best account for most people under 50.",action:"Contribute $"+Math.round(Math.min(583,surplus*0.8))+"/month to a Roth IRA. Max is $7,000/year ($583/month)."},
                  {title:"80/20 Stocks to Bonds for Stability",tip:"Conservative allocation that still grows while reducing volatility.",action:"Vanguard LifeStrategy Conservative Growth (VSCGX) is a single fund with this exact allocation."},
                ],
                medium:[
                  {title:"Three-Fund Portfolio",tip:"US stocks + international stocks + bonds. Simple, diversified, low-cost, and outperforms most actively managed funds.",action:"At Fidelity: FZROX (US), FZILX (international), FXNAX (bonds). Zero expense ratios."},
                  {title:"Roth IRA → 401k Match → HSA → Taxable",tip:"This is the optimal account priority order for most people in their 20s.",action:"Contribute in this order: (1) Roth IRA max, (2) 401k up to employer match, (3) HSA if eligible, (4) taxable brokerage."},
                  {title:"90/10 Stocks to Bonds at Your Age",tip:"With plenty of years until retirement, heavy stock allocation makes sense.",action:"S&P 500 index fund (FXAIX at Fidelity or VOO at Vanguard) for 90%, total bond fund for 10%."},
                ],
                aggressive:[
                  {title:"100% Equities — Maximum Long-Term Growth",tip:"With an aggressive risk tolerance and long time horizon, 100% stocks historically outperforms blended portfolios.",action:"Put everything in VTI (total US market) or split VTI 60% + VXUS 30% + QQQ 10%."},
                  {title:"Max Every Tax-Advantaged Account",tip:"Roth IRA ($7,000) + 401k ($23,000) + HSA ($4,150) = $34,150/year of tax-advantaged space.",action:"Calculate how much you can max. Start with Roth IRA, then 401k beyond match, then HSA."},
                  {title:"Individual Stocks: 10% Rule",tip:"If you want to pick individual stocks, limit it to 10% of portfolio. The rest in low-cost index funds.",action:"Keep 90% in broad index funds. Use up to 10% for individual stock picks if you enjoy the research."},
                ],
              },
            };

            const catRecs=RECS[detailCat];
            if(!catRecs) return null;
            const recs=catRecs[risk]||catRecs["medium"];
            const riskLabel={conservative:"🛡️ Conservative",medium:"⚖️ Balanced",aggressive:"🚀 Aggressive"}[risk]||"⚖️ Balanced";
            const riskColor={conservative:"#0ea5e9",medium:"#f59e0b",aggressive:"#ef4444"}[risk]||"#f59e0b";

            return(
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div style={{fontSize:11,fontWeight:900,color:cat.color,textTransform:"uppercase",letterSpacing:1.5}}>💡 Recommendations</div>
                  <div style={{background:riskColor+"22",border:`1px solid ${riskColor}44`,borderRadius:20,padding:"3px 10px",
                    fontSize:10,fontWeight:900,color:riskColor}}>{riskLabel}</div>
                </div>
                {recs.map((rec,i)=>(
                  <div key={i} style={{background:"#1e293b",borderRadius:14,padding:"14px",border:`1px solid ${cat.color}33`,
                    borderLeft:`4px solid ${cat.color}`}}>
                    <div style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:8}}>
                      <div style={{width:22,height:22,borderRadius:"50%",background:cat.color,
                        display:"flex",alignItems:"center",justifyContent:"center",
                        fontSize:11,fontWeight:900,color:"#fff",flexShrink:0,marginTop:1}}>{i+1}</div>
                      <div style={{fontSize:14,fontWeight:900,color:"#f1f5f9",lineHeight:1.3}}>{rec.title}</div>
                    </div>
                    <div style={{fontSize:12,color:"#94a3b8",fontWeight:700,lineHeight:1.5,marginBottom:10,paddingLeft:32}}>{rec.tip}</div>
                    <div style={{background:"#0f172a",borderRadius:10,padding:"10px 12px",marginLeft:32,
                      border:`1px solid ${cat.color}22`,display:"flex",gap:8,alignItems:"flex-start"}}>
                      <span style={{fontSize:13,flexShrink:0}}>✅</span>
                      <div style={{fontSize:12,fontWeight:800,color:cat.color,lineHeight:1.4}}>{rec.action}</div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
          {/* ── TIMELINE ── */}
          <div style={{background:"#1e293b",borderRadius:14,padding:"14px",border:"1px solid #334155"}}>
            <div style={{fontSize:11,fontWeight:900,color:cat.color,textTransform:"uppercase",letterSpacing:1.5,marginBottom:12}}>Timeline</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {[...pts].reverse().map((pt,i,arr)=>{
                const prev=arr[i+1];
                const diff=prev?pt.v-prev.v:null;
                const good=diff===null?null:(cat.isAsset?diff>0:diff<0);
                return(
                  <div key={pt.date} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 10px",background:"#0f172a",borderRadius:10}}>
                    <div>
                      <div style={{fontSize:12,color:"#475569",fontWeight:800}}>{pt.date}</div>
                      {diff!==null&&diff!==0&&<div style={{fontSize:11,fontWeight:800,color:good?"#10b981":"#ef4444",marginTop:2}}>
                        {good?(cat.isAsset?"▲ +$":"▼ −$"):(cat.isAsset?"▼ −$":"▲ +$")}{Math.abs(diff).toLocaleString()}
                      </div>}
                    </div>
                    <div style={{fontSize:16,fontWeight:900,color:cat.color}}>${pt.v.toLocaleString()}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── DASHBOARD ── */

  // Category ordering from plan priority
  const allCatKeys=Object.keys(data.categories);
  const planOrder=planPriority?.catPriority||allCatKeys;
  const sorted=[...planOrder.filter(k=>allCatKeys.includes(k)),...allCatKeys.filter(k=>!planOrder.includes(k))];
  const assetKeys=sorted.filter(k=>data.categories[k].isAsset);
  const debtKeys=sorted.filter(k=>!data.categories[k].isAsset);

  // Net worth sparkline
  const nwHistory=data.history.map(h=>calcNW(h.snapshot));
  const mn=Math.min(...nwHistory);
  const mx=Math.max(...nwHistory);
  const range=mx-mn||1;
  const SW=220,SH=44;
  const sparkPts=nwHistory.map((v,i)=>({
    x:(nwHistory.length>1?i/(nwHistory.length-1):0.5)*SW,
    y:SH-((v-mn)/range)*(SH-8)-4
  }));
  const sparkLine=sparkPts.map(p=>p.x+","+p.y).join(" ");
  const trendUp=nwHistory.length>1&&nwHistory[nwHistory.length-1]>=nwHistory[0];
  const trendColor=trendUp?"#10b981":"#ef4444";

  // Focus This Month banner
  const focusKey=sorted[0];
  const focusCat=focusKey?data.categories[focusKey]:null;
  const focusStatus=focusKey?getStatus(focusKey):"yellow";
  const focusIsDebt=focusCat&&!focusCat.isAsset;
  const focusTitle=planPriority?.priorityTitle||
    (focusIsDebt&&latest(focusKey)>0?"Pay down your "+focusCat?.label?.toLowerCase():
     focusCat?"Build your "+focusCat?.label?.toLowerCase():"Set your financial baseline");
  const focusSubtext=planPriority
    ?(focusStatus==="red"?"This is costing you money every day — make it your #1 priority this month."
      :focusStatus==="yellow"?"You're making progress. Keep the momentum going."
      :"You're on track here. Maintain your habits and stay consistent.")
    :"Go to 🎯 My Plan to get a personalized strategy and see this populated with your specific guidance.";
  const focusBorderColor=focusStatus==="red"?"#ef444455":focusStatus==="yellow"?"#f59e0b55":"#10b98155";
  const focusTextColor=focusStatus==="red"?"#ef4444":focusStatus==="yellow"?"#f59e0b":"#10b981";

  // Inline editor shared UI
  function EditPanel({catKey}){
    const cat=data.categories[catKey];
    const cur=latest(catKey);
    const amt=parseFloat(inputVal)||0;
    let previewVal=cur;
    if(inputMode==="set") previewVal=amt;
    else if(inputMode==="add") previewVal=cur+amt;
    else if(inputMode==="sub") previewVal=Math.max(0,cur-amt);
    const previewDiff=previewVal-cur;
    const previewGood=cat.isAsset?previewDiff>=0:previewDiff<=0;
    return(
      <div style={{padding:"0 14px 14px",borderTop:"1px solid #1e293b"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,margin:"12px 0 10px"}}>
          {[
            {m:"add",label:cat.isAsset?"+ Add":"+ Charge",color:"#10b981"},
            {m:"sub",label:cat.isAsset?"− Remove":"− Paid",color:"#0ea5e9"},
            {m:"set",label:"= Set Total",color:"#f59e0b"},
          ].map(({m,label,color})=>(
            <button key={m} onClick={()=>{setInputMode(m);setInputVal("");}}
              className="btn-bounce"
              style={{padding:"8px 4px",borderRadius:10,border:`2px solid ${inputMode===m?color:color+"33"}`,
                background:inputMode===m?color+"22":"#0f172a",cursor:"pointer",
                fontSize:12,fontWeight:900,color:inputMode===m?color:"#475569",transition:"all 0.15s"}}>
              {label}
            </button>
          ))}
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:10}}>
          <button onClick={()=>{const v=Math.max(0,(parseFloat(inputVal)||0)-100);setInputVal(String(v));}}
            className="btn-bounce"
            style={{width:40,height:40,borderRadius:10,border:"2px solid #334155",background:"#0f172a",
              fontSize:18,fontWeight:900,color:"#94a3b8",cursor:"pointer",flexShrink:0}}>−</button>
          <input type="number" placeholder="Amount $" value={inputVal}
            onChange={e=>setInputVal(e.target.value)}
            style={{...dinput,textAlign:"center",fontSize:20,padding:"10px"}} autoFocus/>
          <button onClick={()=>{const v=(parseFloat(inputVal)||0)+100;setInputVal(String(v));}}
            className="btn-bounce"
            style={{width:40,height:40,borderRadius:10,border:"2px solid #334155",background:"#0f172a",
              fontSize:18,fontWeight:900,color:"#94a3b8",cursor:"pointer",flexShrink:0}}>+</button>
        </div>
        <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
          {[50,100,250,500,1000].map(a=>(
            <button key={a} onClick={()=>setInputVal(String(a))} className="btn-bounce"
              style={{padding:"5px 10px",borderRadius:8,border:"1px solid #334155",background:"#0f172a",
                fontSize:12,fontWeight:800,color:"#64748b",cursor:"pointer"}}>
              ${a}
            </button>
          ))}
        </div>
        {inputVal!==""&&(
          <div style={{background:"#0f172a",borderRadius:12,padding:"10px 14px",marginBottom:10,
            border:`1px solid ${previewGood?"#10b98144":"#ef444444"}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:12,fontWeight:700,color:"#475569"}}>New total</span>
              <span style={{fontSize:18,fontWeight:900,color:previewGood?"#10b981":"#ef4444"}}>${previewVal.toLocaleString()}</span>
            </div>
            {previewDiff!==0&&(
              <div style={{fontSize:12,fontWeight:800,color:previewGood?"#10b981":"#ef4444",marginTop:3}}>
                {previewDiff>0?"▲ +$":"▼ −$"}{Math.abs(previewDiff).toLocaleString()} {cat.isAsset?(previewDiff>0?"added":"removed"):(previewDiff<0?"paid off":"added")}
              </div>
            )}
          </div>
        )}
        <button onClick={()=>applyChange(catKey)} className="btn-bounce"
          style={{width:"100%",background:`linear-gradient(135deg,${cat.color},${cat.color}aa)`,
            border:"none",borderRadius:12,padding:"13px",fontSize:15,fontWeight:900,
            color:"#fff",cursor:"pointer",boxShadow:`0 4px 16px ${cat.color}44`}}>
          Save ✓
        </button>
      </div>
    );
  }

  // Bar-style category card
  function BarCard({catKey}){
    const cat=data.categories[catKey];
    const cur=latest(catKey);
    const delta=getDelta(catKey);
    const isOpen=activeCard===catKey;
    const isFlash=flashKey===catKey;
    const status=getStatus(catKey);
    const scfg=STATUS[status];
    const slabel=statusLabel(catKey);

    // Progress bar
    let barPct=0;
    let barColor=cat.color;
    let barLabel="";
    let showBar=false;

    if(cat.isAsset&&cat.target>0){
      barPct=Math.min(cur/cat.target,1)*100;
      barColor=status==="green"?"#10b981":status==="yellow"?"#f59e0b":"#ef4444";
      barLabel=`$${cur.toLocaleString()} of $${cat.target.toLocaleString()} goal`;
      showBar=true;
    } else if(!cat.isAsset){
      const peak=data.history.reduce((mx,h)=>Math.max(mx,h.snapshot[catKey]||0),0);
      const baseline=Math.max(data.history[0]?.snapshot[catKey]||0,peak,cur);
      if(cur===0){
        barPct=100;barColor="#10b981";barLabel="No balance — debt free!";showBar=true;
      } else if(baseline>0&&cur<baseline){
        barPct=((baseline-cur)/baseline)*100;
        barColor="#10b981";
        barLabel=`$${cur.toLocaleString()} left · $${(baseline-cur).toLocaleString()} paid off`;
        showBar=true;
      } else if(cur>0){
        barPct=0;barColor="#ef4444";barLabel=`$${cur.toLocaleString()} balance`;showBar=true;
      }
    }

    const borderColor=isFlash?"#10b981":status==="red"?"#ef444440":status==="yellow"?"#f59e0b30":"#1e293b";

    return(
      <div style={{
        background:isFlash?"#10b98112":"#1e293b",
        border:`2px solid ${borderColor}`,
        borderRadius:16,overflow:"hidden",
        transition:"border-color 0.3s,background 0.3s"
      }}>
        {/* Header */}
        <div style={{padding:"14px",display:"flex",alignItems:"center",gap:12}}>
          {/* Emoji icon */}
          <div onClick={()=>{if(!isOpen){setDetailCat(catKey);setView("detail");}}}
            style={{width:46,height:46,borderRadius:13,background:cat.color+"22",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:24,flexShrink:0,cursor:"pointer"}}>
            {cat.emoji}
          </div>

          {/* Label + value */}
          <div onClick={()=>{if(!isOpen){setDetailCat(catKey);setView("detail");}}}
            style={{flex:1,minWidth:0,cursor:"pointer"}}>
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3,flexWrap:"wrap"}}>
              <span style={{fontSize:13,fontWeight:900,color:"#f1f5f9"}}>{cat.label}</span>
              <span style={{
                background:scfg.bg,border:`1px solid ${scfg.border}`,
                borderRadius:20,padding:"2px 9px",
                fontSize:10,fontWeight:900,color:scfg.text,whiteSpace:"nowrap",lineHeight:1.6
              }}>{slabel}</span>
            </div>
            <div style={{fontSize:22,fontWeight:900,color:isFlash?"#10b981":cat.color,lineHeight:1,transition:"color 0.3s"}}>
              ${cur.toLocaleString()}
            </div>
            {delta&&delta.diff!==0&&!isOpen&&(
              <div style={{fontSize:11,fontWeight:800,color:delta.good?"#10b981":"#ef4444",marginTop:2}}>
                {cat.isAsset?(delta.good?"▲ +$":"▼ −$"):(delta.good?"▼ −$":"▲ +$")}{Math.abs(delta.diff).toLocaleString()} since last
              </div>
            )}
          </div>

          {/* Edit button */}
          <button onClick={(e)=>{
              e.stopPropagation();
              if(isOpen){setActiveCard(null);setInputVal("");setInputMode("set");}
              else{setActiveCard(catKey);setInputVal("");setInputMode("set");}
            }} className="btn-bounce"
            style={{width:38,height:38,borderRadius:12,border:`2px solid ${cat.color}44`,
              background:isOpen?cat.color+"33":"#0f172a",cursor:"pointer",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:16,color:cat.color,flexShrink:0,transition:"background 0.2s"}}>
            {isOpen?"✕":"✏️"}
          </button>
        </div>

        {/* Progress bar */}
        {showBar&&!isOpen&&(
          <div style={{padding:"0 14px 14px"}}>
            <div style={{background:"#0f172a",borderRadius:20,height:14,overflow:"hidden",marginBottom:6}}>
              <div style={{
                width:`${Math.max(barPct,barPct>0?3:0)}%`,height:"100%",
                background:`linear-gradient(90deg,${barColor},${barColor}bb)`,
                borderRadius:20,transition:"width 0.6s ease"
              }}/>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:11,color:"#64748b",fontWeight:700,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",paddingRight:8}}>{barLabel}</span>
              {barPct>0&&<span style={{fontSize:12,fontWeight:900,color:barColor,flexShrink:0}}>{Math.round(barPct)}%</span>}
            </div>
          </div>
        )}

        {/* No-target nudge for assets */}
        {!showBar&&!isOpen&&cat.isAsset&&cur===0&&(
          <div style={{padding:"0 14px 12px"}}>
            <div style={{fontSize:11,color:"#334155",fontWeight:700}}>Tap ✏️ to add a balance and set a goal</div>
          </div>
        )}

        {/* Inline editor */}
        {isOpen&&<EditPanel catKey={catKey}/>}
      </div>
    );
  }

  return(
    <div style={{fontFamily:"Nunito",background:"#0f172a",minHeight:"100vh",paddingBottom:120}}>

      {/* Header */}
      <div style={{background:"linear-gradient(135deg,#1e3a5f,#0f172a)",padding:"14px 16px 12px",
        borderBottom:"1px solid #1e293b",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:11,color:"#64748b",fontWeight:800,textTransform:"uppercase",letterSpacing:2}}>Wealth Tracker</div>
          <div style={{fontSize:11,color:"#334155",fontWeight:700,marginTop:2}}>
            {data.history.length} check-in{data.history.length!==1?"s":""} · Last: {data.history[data.history.length-1]?.date}
            {data.history[data.history.length-1]?.note==="Imported from My Plan"&&
              <span style={{color:"#f59e0b",marginLeft:6}}>· from plan ✓</span>}
          </div>
        </div>
        <button onClick={()=>setView("history")} className="btn-bounce"
          style={{background:"#1e293b",border:"1px solid #334155",borderRadius:10,
            padding:"6px 12px",fontSize:11,fontWeight:800,color:"#64748b",cursor:"pointer"}}>
          History →
        </button>
      </div>

      <div style={{padding:"14px 16px",display:"flex",flexDirection:"column",gap:16}}>

        {/* ── NET WORTH HERO ── */}
        <div style={{background:"linear-gradient(135deg,#0f172a,#1e293b)",borderRadius:20,
          padding:"18px 20px",border:"1px solid #334155"}}>
          <div style={{fontSize:11,color:"#64748b",fontWeight:800,textTransform:"uppercase",
            letterSpacing:2,marginBottom:8}}>Total Net Worth</div>
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:42,fontWeight:900,fontFamily:"Fredoka One",lineHeight:1,
                color:curNW>=0?"#10b981":"#ef4444"}}>
                {curNW>=0?"$":"−$"}{Math.abs(Math.round(curNW)).toLocaleString()}
              </div>
              {nwDelta!==null&&(
                <div style={{display:"inline-flex",alignItems:"center",gap:5,marginTop:8,
                  background:nwDelta>=0?"#10b98120":"#ef444420",
                  border:`1px solid ${nwDelta>=0?"#10b981":"#ef4444"}`,
                  borderRadius:20,padding:"4px 12px"}}>
                  <span style={{fontSize:13,fontWeight:900,color:nwDelta>=0?"#10b981":"#ef4444"}}>
                    {nwDelta>=0?"▲ +$":"▼ −$"}{Math.abs(Math.round(nwDelta)).toLocaleString()} since last update
                  </span>
                </div>
              )}
              <div style={{display:"flex",gap:20,marginTop:14}}>
                <div>
                  <div style={{fontSize:15,fontWeight:900,color:"#10b981"}}>${Math.round(totalAssets).toLocaleString()}</div>
                  <div style={{fontSize:10,color:"#475569",fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>assets</div>
                </div>
                <div style={{width:1,background:"#334155"}}/>
                <div>
                  <div style={{fontSize:15,fontWeight:900,color:totalDebt===0?"#10b981":"#ef4444"}}>
                    {totalDebt===0?"$0 ✓":"$"+Math.round(totalDebt).toLocaleString()}
                  </div>
                  <div style={{fontSize:10,color:"#475569",fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>debt</div>
                </div>
              </div>
            </div>
            {/* Mini sparkline */}
            {nwHistory.length>1&&(
              <div style={{flexShrink:0,display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}>
                <svg viewBox={`0 0 ${SW} ${SH}`} style={{width:88,height:36,overflow:"visible"}}>
                  <defs>
                    <linearGradient id="spkg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={trendColor} stopOpacity="0.3"/>
                      <stop offset="100%" stopColor={trendColor} stopOpacity="0"/>
                    </linearGradient>
                  </defs>
                  <polygon points={`0,${SH} ${sparkLine} ${SW},${SH}`} fill="url(#spkg)"/>
                  <polyline points={sparkLine} fill="none" stroke={trendColor} strokeWidth="2.5" strokeLinejoin="round"/>
                  {sparkPts.map((p,i)=>(
                    <circle key={i} cx={p.x} cy={p.y} r="3" fill={trendColor} stroke="#0f172a" strokeWidth="2"/>
                  ))}
                </svg>
                <div style={{fontSize:10,fontWeight:800,color:trendColor}}>
                  {trendUp?"▲ trending up":"▼ trending down"}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── FOCUS THIS MONTH ── */}
        <div style={{borderRadius:16,padding:"16px",
          border:`2px solid ${focusBorderColor}`,
          background:"#1e293b"}}>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
            <span style={{fontSize:18}}>🎯</span>
            <span style={{fontSize:11,fontWeight:900,color:focusTextColor,
              textTransform:"uppercase",letterSpacing:1.5}}>Focus This Month</span>
          </div>
          <div style={{fontSize:16,fontWeight:900,color:"#f1f5f9",lineHeight:1.35,marginBottom:6}}>
            {focusTitle}
          </div>
          <div style={{fontSize:12,color:"#94a3b8",fontWeight:700,lineHeight:1.6}}>
            {focusSubtext}
          </div>
          {planPriority&&(
            <div style={{marginTop:8,fontSize:10,color:"#334155",fontWeight:700}}>
              From your plan · {planPriority.generatedAt}
            </div>
          )}
        </div>

        {/* ── ASSETS ── */}
        <div>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
            <span style={{fontSize:12,fontWeight:900,color:"#10b981",
              textTransform:"uppercase",letterSpacing:1.5}}>💰 Assets</span>
            <div style={{flex:1,height:1,background:"#1e293b"}}/>
            <span style={{fontSize:12,fontWeight:900,color:"#10b981"}}>
              ${Math.round(totalAssets).toLocaleString()}
            </span>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {assetKeys.map(k=><BarCard key={k} catKey={k}/>)}
          </div>
        </div>

        {/* ── DEBTS ── */}
        <div>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
            <span style={{fontSize:12,fontWeight:900,color:"#ef4444",
              textTransform:"uppercase",letterSpacing:1.5}}>💳 Debts</span>
            <div style={{flex:1,height:1,background:"#1e293b"}}/>
            <span style={{fontSize:12,fontWeight:900,color:totalDebt===0?"#10b981":"#ef4444"}}>
              {totalDebt===0?"$0 — debt free! 🎉":"$"+Math.round(totalDebt).toLocaleString()}
            </span>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {debtKeys.map(k=><BarCard key={k} catKey={k}/>)}
          </div>
        </div>

        {/* Plan nudge if not linked */}
        {!planPriority&&(
          <div style={{background:"#1e293b",borderRadius:12,padding:"12px 14px",
            border:"1px solid #0ea5e933",display:"flex",gap:10,alignItems:"center"}}>
            <span style={{fontSize:16,flexShrink:0}}>💡</span>
            <div style={{fontSize:12,color:"#64748b",fontWeight:700,lineHeight:1.5}}>
              <span style={{color:"#0ea5e9"}}>Go to 🎯 My Plan</span> to get a personalized strategy — it will populate your Focus This Month and prioritize your cards automatically.
            </div>
          </div>
        )}

        <button onClick={()=>{
          if(window.confirm("Reset all tracking data and plan setup? This cannot be undone.")){
            setPlanPriority(null);
            setSetupVals({});
            setSetupTargets({});
            onResetPro();
          }
        }}
          style={{background:"none",border:"none",color:"#334155",fontSize:12,
            fontWeight:700,cursor:"pointer",padding:"8px",textAlign:"center"}}>
          Reset tracker
        </button>

      </div>
    </div>
  );
}
